<template>
  <section id="products" ref="section" class="product-parallax" aria-labelledby="products-heading">
    <header class="gallery-heading">
      <p class="gallery-eyebrow">{{ language === 'zh' ? '企业产品 / PRODUCT GALLERY' : 'BUSINESS PRODUCTS / GALLERY' }}</p>
      <h2 id="products-heading">{{ copy.spotlightTitle }}</h2>
      <p class="gallery-description">{{ copy.spotlightDesc }}</p>
    </header>

    <div ref="viewport" class="gallery-viewport">
      <div ref="wall" class="gallery-wall">
        <div v-for="(row, index) in rows" :key="index" class="gallery-row" :class="{ 'gallery-row-secondary': index === 1 }">
          <figure v-for="product in row" :key="product.id" class="gallery-card">
            <span class="card-image"><img :src="product.image" :alt="product.title" loading="lazy" decoding="async" :width="product.width" :height="product.height" draggable="false"></span>
            <figcaption class="card-caption"><span><span class="card-category">{{ product.category }}</span><strong>{{ product.title }}</strong></span></figcaption>
          </figure>
        </div>
      </div>
    </div>

    <div ref="footer" class="gallery-footer">
      <p>{{ labels.hint }}</p>
      <a class="gallery-cta" href="#contact"><span>{{ copy.cta }}</span><span class="cta-arrow material-symbols-rounded" aria-hidden="true">arrow_forward</span></a>
    </div>

  </section>
</template>

<script>
import dashboardWater from '@/assets/数据大屏1.png'
import dashboardGovernance from '@/assets/数据大屏2.png'
import dashboardTraffic from '@/assets/数据大屏3.png'
import queryDialogue from '@/assets/智能问数-数据对话.png'
import queryRendering from '@/assets/智能问数-数据渲染.png'
import queryPermissions from '@/assets/智能问数-表字段权限控制.jpg'
import managementPortal from '@/assets/管理系统-门户.png'
import managementCrm from '@/assets/管理系统-crm.png'
import managementDingTalk from '@/assets/管理系统-钉钉.png'

export default {
  name: 'ProductParallax',
  props: { language: { type: String, default: 'zh' }, copy: { type: Object, required: true } },
  data: () => ({
    frameId: null, observer: null, sizeObserver: null, motionQuery: null, nearViewport: true, disposed: false,
    rowProducts: [
      ['dashboard-water', 'dashboard-governance', 'dashboard-traffic'],
      ['query-dialogue', 'query-rendering', 'query-permissions'],
      ['management-portal', 'management-crm', 'management-dingtalk']
    ]
  }),
  computed: {
    labels() {
      return this.language === 'zh'
        ? { hint: '管理协同 · 数据呈现 · 智能问数' }
        : { hint: 'Team management · Data presentation · Conversational analytics' }
    },
    products() {
      const zh = this.language === 'zh'
      return [
        { id: 'dashboard-water', image: dashboardWater, width: 1620, height: 915, category: zh ? '数据大屏 / DATA DASHBOARD' : 'DATA DASHBOARD', title: zh ? '水质实时监测预警' : 'Real-time water quality monitoring', description: zh ? '汇总水质监测、污染预警与区域指标。' : 'Bring water quality monitoring, alerts and regional indicators together.' },
        { id: 'dashboard-governance', image: dashboardGovernance, width: 1670, height: 823, category: zh ? '数据大屏 / DATA DASHBOARD' : 'DATA DASHBOARD', title: zh ? '数据治理全景分析' : 'Data governance overview', description: zh ? '集中呈现数据接入、治理与资产使用情况。' : 'See data ingestion, governance and asset usage in one view.' },
        { id: 'dashboard-traffic', image: dashboardTraffic, width: 1608, height: 704, category: zh ? '数据大屏 / DATA DASHBOARD' : 'DATA DASHBOARD', title: zh ? '行业情况深度分析' : 'Industry insights and analysis', description: zh ? '通过地图、指标与趋势，综合分析行业运行情况。' : 'Analyze industry conditions through maps, metrics and trends.' },
        { id: 'query-dialogue', image: queryDialogue, width: 2549, height: 1404, category: zh ? '智能问数 / AI DATA' : 'CONVERSATIONAL ANALYTICS', title: zh ? '用自然语言，查询业务数据' : 'Ask your business data', description: zh ? '在账号权限范围内提问，并查看图表与数据明细。' : 'Ask questions within account permissions and review charts and details.' },
        { id: 'query-rendering', image: queryRendering, width: 2549, height: 1473, category: zh ? '智能问数 / AI DATA' : 'CONVERSATIONAL ANALYTICS', title: zh ? '按场景配置结果呈现' : 'Configure how results are presented', description: zh ? '为不同查询场景设置图表、指标卡或表格。' : 'Choose charts, metric cards or tables for different query scenarios.' },
        { id: 'query-permissions', image: queryPermissions, width: 1670, height: 823, category: zh ? '智能问数 / AI DATA' : 'CONVERSATIONAL ANALYTICS', title: zh ? '精细化数据访问治理' : 'Fine-grained data access governance', description: zh ? '基于用户与角色，精细配置可访问的数据表与字段范围。' : 'Define table- and field-level access for individual users and roles.' },
        { id: 'management-portal', image: managementPortal, width: 1670, height: 823, category: zh ? '管理系统 / MANAGEMENT' : 'MANAGEMENT SYSTEM', title: zh ? '企业应用统一门户' : 'Unified enterprise application portal', description: zh ? '从统一入口访问企业应用与工作信息。' : 'Access enterprise applications and work information from one portal.' },
        { id: 'management-crm', image: managementCrm, width: 1670, height: 823, category: zh ? '管理系统 / MANAGEMENT' : 'MANAGEMENT SYSTEM', title: zh ? '集中管理客户信息' : 'Manage customer information in one place', description: zh ? '集中维护客户资料、联系人与跟进状态。' : 'Manage customer profiles, contacts and follow-up status together.' },
        { id: 'management-dingtalk', image: managementDingTalk, width: 2549, height: 1473, category: zh ? '管理系统 / MANAGEMENT' : 'MANAGEMENT SYSTEM', title: zh ? '连接钉钉，同步组织架构' : 'Sync your organization with DingTalk', description: zh ? '连接钉钉组织架构，统一管理账号与角色。' : 'Connect DingTalk organization data and manage accounts and roles.' }
      ]
    },
    rows() { return this.rowProducts.map(row => row.map(id => this.products.find(product => product.id === id))) }
  },
  mounted() {
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce), (max-width: 760px), (pointer: coarse)')
    this.motionQuery.addEventListener('change', this.queueMeasure)
    window.addEventListener('scroll', this.queueMeasure, { passive: true })
    window.addEventListener('resize', this.queueMeasure, { passive: true })
    this.observer = new IntersectionObserver(entries => {
      this.nearViewport = entries[0].isIntersecting
      if (this.nearViewport) this.queueMeasure()
    }, { rootMargin: '160px' })
    this.observer.observe(this.$refs.section)
    this.sizeObserver = new ResizeObserver(this.queueMeasure)
    this.sizeObserver.observe(this.$refs.section)
    this.queueMeasure()
  },
  beforeUnmount() {
    this.disposed = true
    window.removeEventListener('scroll', this.queueMeasure)
    window.removeEventListener('resize', this.queueMeasure)
    this.motionQuery?.removeEventListener('change', this.queueMeasure)
    this.observer?.disconnect()
    this.sizeObserver?.disconnect()
    if (this.frameId !== null) window.cancelAnimationFrame(this.frameId)
  },
  methods: {
    queueMeasure() {
      if (this.disposed || this.frameId !== null || (!this.nearViewport && !this.motionQuery?.matches)) return
      this.frameId = window.requestAnimationFrame(() => { this.frameId = null; this.measure() })
    },
    measure() {
      const wall = this.$refs.wall
      if (!wall) return
      if (this.motionQuery?.matches) {
        wall.style.removeProperty('--wall-tilt')
        wall.style.removeProperty('--wall-turn')
        wall.style.removeProperty('--wall-lift')
        wall.style.removeProperty('--wall-opacity')
        wall.style.removeProperty('--row-shift')
        wall.style.removeProperty('--row-return')
        wall.style.removeProperty('--wall-scale')
        wall.style.removeProperty('--row-float')
        wall.style.removeProperty('--row-angle')
        this.$refs.viewport.style.removeProperty('--feather-opacity')
        this.$refs.viewport.style.removeProperty('--exit-feather-opacity')
        for (const name of ['--heading-opacity', '--footer-opacity', '--footer-lift', '--footer-visibility', '--footer-visibility-delay']) {
          this.$refs.section.style.removeProperty(name)
        }
        return
      }
      if (!this.nearViewport) return
      const { top, height } = this.$refs.section.getBoundingClientRect()
      if (height <= 0) return
      const footer = this.$refs.footer
      // Read the untransformed layout before writing styles. The next section's
      // background paints over content overflowing this isolated section.
      const footerRoom = Math.max(0, height - footer.offsetTop - footer.offsetHeight - 48)
      // Let the tilted gallery develop before a longer, partial fade.
      // Keep a faint backdrop at the handoff rather than fading to empty space.
      const unfoldDistance = Math.max(500, window.innerHeight * .85)
      const travel = Math.max(0, 78 - top)
      const progress = travel / unfoldDistance
      const smooth = value => {
        const clamped = Math.max(0, Math.min(1, value))
        return clamped * clamped * (3 - 2 * clamped)
      }
      const eased = smooth(progress / 1.2)
      const exit = smooth((progress - .8) / .95)
      const drift = smooth((progress - .8) / 1.8)
      const footerIn = smooth((progress - 1.05) / .6)
      const footerOut = smooth((progress - 1.85) / .5)
      const footerOpacity = footerIn * (1 - footerOut)
      const requestedFooterLift = Math.min(travel, unfoldDistance * 2.35) * .75
        + (1 - footerIn) * 24 - footerOut * 20
      // Ease the footer to a stop INSIDE this section, before the next background
      // can cover it. Its opacity remains free to finish the existing exit phase.
      const footerBrake = Math.min(96, footerRoom / 2)
      const footerBrakeStart = footerRoom - footerBrake
      const footerRelease = footerBrake > 0
        ? Math.max(0, Math.min(1, (requestedFooterLift - footerBrakeStart) / (footerBrake * 2)))
        : 1
      const footerLift = Math.min(requestedFooterLift, footerBrakeStart)
        + footerBrake * 2 * (footerRelease - footerRelease ** 3 + .5 * footerRelease ** 4)
      // Ease out the counter-scroll instead of abruptly stopping the wall.
      const release = Math.max(0, Math.min(1, (progress - 1.8) / .8))
      const compensation = Math.min(travel, unfoldDistance * 1.8)
        + unfoldDistance * .8 * (release - release ** 3 + .5 * release ** 4)
      wall.style.setProperty('--wall-tilt', `${32 - eased * 18 + drift * 10}deg`)
      wall.style.setProperty('--wall-turn', `${(1 - eased) * 14 - drift * 6}deg`)
      wall.style.setProperty('--wall-lift', `${compensation - 40 - eased * 76}px`)
      wall.style.setProperty('--wall-opacity', `${.18 + smooth(progress / .8) * .47 - exit * .49}`)
      wall.style.setProperty('--wall-scale', `${1 - drift * .06}`)
      wall.style.setProperty('--row-shift', `${-140 * (1 - eased) - drift * 150}px`)
      wall.style.setProperty('--row-return', `${140 * (1 - eased) + drift * 180}px`)
      wall.style.setProperty('--row-float', `${drift * -36}px`)
      wall.style.setProperty('--row-angle', `${drift * -1.8}deg`)
      this.$refs.viewport.style.setProperty('--feather-opacity', `${1 - eased}`)
      this.$refs.viewport.style.setProperty('--exit-feather-opacity', `${.35 + exit * .65}`)
      this.$refs.section.style.setProperty('--heading-opacity', `${1 - smooth((progress - .65) / .65)}`)
      this.$refs.section.style.setProperty('--footer-opacity', `${footerOpacity}`)
      this.$refs.section.style.setProperty('--footer-lift', `${footerLift}px`)
      // Let the opacity transition finish before hiding, even on a fast scroll.
      // Restore visibility immediately when scrolling back into the scene.
      this.$refs.section.style.setProperty('--footer-visibility', footerOpacity > 0 ? 'visible' : 'hidden')
      this.$refs.section.style.setProperty('--footer-visibility-delay', footerOpacity > 0 ? '0s' : '.45s')
    }
  }
}
</script>

<style scoped>
.product-parallax{position:relative;margin-top:clamp(48px,5vw,96px);padding:0;background:var(--page);color:var(--ink);isolation:isolate}
/* Intro and wall share the same space: the title sits IN FRONT of the subdued screens. */
.gallery-heading{position:absolute;z-index:2;top:clamp(160px,22vh,280px);inset-inline:0;width:min(var(--content-max-width),calc(100% - 96px));margin:0 auto;text-align:left;pointer-events:none;opacity:var(--heading-opacity,1);will-change:opacity}
.gallery-eyebrow{margin-bottom:24px;font-size:11px;letter-spacing:.16em;color:var(--muted)}
.gallery-heading h2{max-width:1040px;font-size:clamp(44px,4.5vw,80px);line-height:1.2;font-weight:600;letter-spacing:-.045em;text-wrap:balance}
.gallery-description{max-width:760px;margin:28px 0 0;color:var(--muted);font-size:clamp(16px,1.35vw,20px);line-height:1.9;text-wrap:balance}
/* A bounded scene, not three full rows plus a long empty scroll tail. */
.gallery-viewport{position:relative;height:calc(100vh + max(600px,110vh));perspective:1400px;overflow:hidden;overflow:clip}
/* Theme-colored feather softens the service/product boundary and retreats on scroll. */
.gallery-viewport::before{content:'';position:absolute;inset:0 0 auto;height:clamp(240px,32vh,400px);z-index:1;pointer-events:none;background:linear-gradient(180deg,var(--page) 0%,var(--page) 12%,transparent 100%);opacity:var(--feather-opacity,1)}
.gallery-viewport::after{content:'';position:absolute;inset:auto 0 0;height:clamp(300px,48vh,760px);z-index:1;pointer-events:none;background:linear-gradient(180deg,transparent,var(--page) 85%,var(--page));opacity:var(--exit-feather-opacity,.35)}
.gallery-wall{position:absolute;top:160px;inset-inline:0;width:min(var(--content-max-width),calc(100% - 96px));margin:0 auto;opacity:var(--wall-opacity,.18);transform:translateY(var(--wall-lift,-40px)) rotateX(var(--wall-tilt,32deg)) rotateZ(var(--wall-turn,14deg)) scale(var(--wall-scale,1));transform-origin:center top;will-change:transform,opacity}
.gallery-row{display:flex;gap:clamp(22px,2.8vw,44px);transform:translate3d(var(--row-shift,-140px),var(--row-float,0px),0) rotateZ(var(--row-angle,0deg));will-change:transform}
.gallery-row+.gallery-row{margin-top:clamp(28px,3vw,48px)}
.gallery-row-secondary{transform:translate3d(var(--row-return,140px),calc(var(--row-float,0px) * -1),0) rotateZ(calc(var(--row-angle,0deg) * -1))}
.gallery-card{display:block;min-width:0;flex:1;border:1px solid var(--line-strong);margin:0;padding:0;border-radius:18px;overflow:hidden;text-align:left;background:var(--page);color:var(--ink);cursor:default;box-shadow:0 24px 48px -28px rgba(0,0,0,.35);transition:translate .4s cubic-bezier(.22,1,.36,1),border-color .3s,box-shadow .3s}
.card-image{display:block;aspect-ratio:1.78;overflow:hidden;background:var(--page)}
.card-image img{display:block;width:100%;height:100%;object-fit:contain;opacity:.84;transition:opacity .4s}
.card-caption{display:flex;align-items:center;justify-content:space-between;gap:20px;padding:20px 24px;background:var(--page);transition:background-color .9s}
.card-category{display:block;color:var(--muted);font-size:10px;letter-spacing:.1em;line-height:1.5}
.card-caption strong{display:block;margin-top:7px;font-size:clamp(15px,1.25vw,19px);line-height:1.5;font-weight:500}
@media(hover:hover) and (pointer:fine){.gallery-card:hover{translate:0 -9px;border-color:var(--muted);box-shadow:0 32px 60px -28px rgba(0,0,0,.45)}.gallery-card:hover img{opacity:1}}
.gallery-footer{position:absolute;z-index:3;top:calc(86vh - 78px);inset-inline:0;text-align:center;padding:0 24px;opacity:var(--footer-opacity,0);visibility:var(--footer-visibility,hidden);transform:translateY(var(--footer-lift,24px));will-change:transform,opacity;pointer-events:none;transition:opacity .45s ease-out,visibility 0s var(--footer-visibility-delay,0s)}
.gallery-footer>p{color:var(--muted);font-size:14px;letter-spacing:.08em;margin-bottom:24px}
.gallery-footer .gallery-cta{pointer-events:auto}
.gallery-cta{display:inline-flex;align-items:center;min-height:50px;padding:0 26px;border:1px solid var(--line-strong);border-radius:99px;text-decoration:none;color:var(--muted);font-size:14px;transition:color .3s,border-color .3s}
.cta-arrow{width:0;margin-left:0;opacity:0;overflow:hidden;font-size:20px;transform:translateX(-6px);transition:width .35s,margin-left .35s,opacity .25s,transform .35s}
.gallery-cta:focus-visible{outline:2px solid var(--accent);outline-offset:5px;color:var(--ink)}
.gallery-cta:focus-visible .cta-arrow{width:20px;margin-left:12px;opacity:1;transform:none}
@media(hover:hover){.gallery-cta:hover{color:var(--ink);border-color:var(--muted)}.gallery-cta:hover .cta-arrow{width:20px;margin-left:12px;opacity:1;transform:none}}
@media(min-width:761px){.gallery-viewport{mask-image:linear-gradient(90deg,transparent,#000 20px,#000 calc(100% - 20px),transparent)}}
@media(max-width:980px){.gallery-heading{width:calc(100% - 48px)}.card-caption{padding:16px}}
@media(max-width:760px),(pointer:coarse){
  .product-parallax{margin-top:0;padding:76px 0 64px}
  .gallery-heading{position:relative;top:auto;width:calc(100% - 36px);pointer-events:auto;opacity:1;will-change:auto}
  .gallery-heading h2{font-size:clamp(28px,6vw,44px)}
  .gallery-eyebrow{font-size:10px;margin-bottom:18px}
  .gallery-description{font-size:14px}
  .gallery-viewport{height:auto;margin-top:24px;padding:0;perspective:none;mask-image:none}
  .gallery-viewport::before,.gallery-viewport::after{display:none}
  .gallery-wall{position:relative;top:auto;left:auto;width:100%;margin:0;opacity:1;transform:none!important;will-change:auto}
  .gallery-row,.gallery-row-secondary{display:flex;gap:18px;padding:20px 18px;overflow-x:auto;scroll-snap-type:x mandatory;scroll-padding-inline:18px;transform:none;will-change:auto}
  .gallery-row+.gallery-row{margin-top:0}
  .gallery-card,.gallery-row-secondary .gallery-card{flex:none;width:min(82vw,560px);border-radius:14px;scroll-snap-align:center}
  .card-image img{opacity:1}
  .card-caption{padding:16px}.card-caption strong{font-size:15px}
  .gallery-footer{position:relative;top:auto;margin-top:16px;opacity:1;visibility:visible;transform:none;will-change:auto;transition:none}
}
@media(prefers-reduced-motion:reduce){
  .product-parallax{margin-top:0;padding:76px 0 64px}
  .gallery-heading{position:relative;top:auto;pointer-events:auto;opacity:1;will-change:auto}
  .gallery-viewport{height:auto;padding-top:48px;padding-bottom:48px}
  .gallery-viewport::before,.gallery-viewport::after{display:none}
  .gallery-wall{position:relative;top:auto;left:auto;width:calc(100% - 48px);margin:0 auto}
  .gallery-footer{position:relative;top:auto;opacity:1;visibility:visible;transform:none;will-change:auto;transition:none}
  .gallery-wall,.gallery-row,.gallery-row-secondary{opacity:1;transform:none!important;will-change:auto}
  .gallery-card,.card-image img,.card-caption,.gallery-cta,.cta-arrow{transition:none}
  .gallery-card:hover{translate:none}
}
@media(min-width:981px){
  .product-parallax{margin-top:clamp(38px,4vw,77px)}
  .gallery-eyebrow{margin-bottom:19px;font-size:9px}
  .gallery-heading h2{max-width:840px;font-size:clamp(36px,3.6vw,64px)}
  .gallery-description{max-width:610px;margin-top:22px;font-size:clamp(14px,1.08vw,16px);line-height:1.8}
  .gallery-row{gap:clamp(18px,2.24vw,35px)}
  .gallery-card{border-radius:14px}
  .card-caption{gap:16px;padding:16px 19px}
  .card-category{font-size:8px}
  .card-caption strong{margin-top:6px;font-size:clamp(12px,1vw,15px)}
  .gallery-footer>p{margin-bottom:19px;font-size:11px}
  .gallery-cta{min-height:40px;padding:0 21px;font-size:11px}
  .cta-arrow{font-size:16px}
}
@media(max-width:980px){
  .gallery-heading h2{font-size:clamp(26px,4.8vw,35px)}
  .gallery-description{font-size:13px}
  .card-category{font-size:8px}
  .card-caption strong{font-size:13px}
  .gallery-footer>p{font-size:12px}
  .gallery-cta{min-height:44px;padding:0 22px;font-size:12px}
  .cta-arrow{font-size:16px}
}
</style>
